import type { UnifiedProtocolUsage } from './unifiedProtocolUsage';
import { authFetch } from '../../utils/api';
import type { ProtocolSource, ProtocolUsage } from './protocolUsage';
import type {
  DeviceUsageRow,
  MemberListRow,
  OperationsOnline,
  OperationsSummary,
  RedemptionRow,
  SnapshotList,
  TopAccountsPage,
  KshieldLegacyObservation,
} from './lib';
import { OperationsApiError, retryProjectionConflict, topAccountDevicePath, topAccountsPath } from './lib';

const BASE = '/api/admin/operations';
export type OperationsCapabilities = { canMemberDetail: boolean; canClearAccount: boolean };

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await authFetch(path, { signal });
  let body: { code?: number; msg?: string; data?: T };
  try {
    body = await response.json();
  } catch {
    throw new Error('營運數據回應格式錯誤');
  }
  if (!response.ok || body.code !== 0 || body.data === null || body.data === undefined) {
    throw new OperationsApiError(body.msg || `營運數據讀取失敗 (${response.status})`, response.status);
  }
  return body.data;
}

export function loadSummary(brand: string, from: string, through: string, signal?: AbortSignal): Promise<OperationsSummary> {
  const params = new URLSearchParams({ brand, from, through });
  return retryProjectionConflict(() => get(`${BASE}/summary?${params.toString()}`, signal), signal);
}

export function loadKshieldLegacyObservations(from: string, through: string, clearNames: boolean, signal?: AbortSignal): Promise<KshieldLegacyObservation> {
  const params = new URLSearchParams({ brand: 'kshield', from, through, clearNames: clearNames ? '1' : '0' });
  return get(`${BASE}/kshield-legacy-observations?${params}`, signal);
}

export function loadOnline(brand: string, signal?: AbortSignal): Promise<OperationsOnline> {
  return get(`${BASE}/online?${new URLSearchParams({ brand })}`, signal);
}

export function loadOperationsCapabilities(brand: string, signal?: AbortSignal): Promise<OperationsCapabilities> {
  return get(`${BASE}/capabilities?${new URLSearchParams({ brand })}`, signal);
}

export function loadTopAccounts(brand: string, page: number, pageSize: number, snapshotId?: string | null, signal?: AbortSignal, clearNames = false): Promise<TopAccountsPage> {
  const path = topAccountsPath(brand, page, pageSize, snapshotId);
  const requestPath = clearNames ? `${path}&clearNames=1` : path;
  return snapshotId ? get(requestPath, signal) : retryProjectionConflict(() => get(requestPath, signal), signal);
}

export function loadDailyMembers(brand: string, date: string, snapshotId: string, metric: string, cursor?: string | null, signal?: AbortSignal, clearNames = false): Promise<SnapshotList<MemberListRow>> {
  const params = new URLSearchParams({ brand, snapshotId, metric });
  if (cursor) params.set('cursor', cursor);
  if (clearNames) params.set('clearNames', '1');
  return get(`${BASE}/daily/${encodeURIComponent(date)}/members?${params}`, signal);
}

export function loadCohortRedemptions(brand: string, date: string, snapshotId: string, cursor?: string | null, signal?: AbortSignal, clearNames = false): Promise<SnapshotList<RedemptionRow>> {
  const params = new URLSearchParams({ brand, snapshotId });
  if (cursor) params.set('cursor', cursor);
  if (clearNames) params.set('clearNames', '1');
  return get(`${BASE}/cohorts/${encodeURIComponent(date)}/redemptions?${params}`, signal);
}

export function loadTopAccountDevices(brand: string, memberId: number, snapshotId: string, cursor?: string | null, signal?: AbortSignal): Promise<SnapshotList<DeviceUsageRow>> {
  return get(topAccountDevicePath(brand, memberId, snapshotId, cursor), signal);
}

export function loadProtocolUsage(brand: string, source: ProtocolSource, from: string, through: string, signal?: AbortSignal): Promise<ProtocolUsage> {
  const params = new URLSearchParams({ brand, source, from, through });
  return get(`${BASE}/protocol-usage?${params}`, signal);
}

export function loadUnifiedProtocolUsage(brand: string, from: string, through: string, signal?: AbortSignal): Promise<UnifiedProtocolUsage> {
  return get(`${BASE}/protocol-usage/unified?${new URLSearchParams({brand, from, through})}`, signal);
}
